import { useEffect, useMemo, useState } from "react";
import { Plus, Search, ChevronRight, Pencil, Trash2, Shield, FileText, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { initials, formatApiError } from "../lib/format";

function ClientDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", document: "", phone: "", email: "", address: "", notes: "", portal_user_email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({
      name: initial?.name || "",
      document: initial?.document || "",
      phone: initial?.phone || "",
      email: initial?.email || "",
      address: initial?.address || "",
      notes: initial?.notes || "",
      portal_user_email: "",
    });
  }, [open, initial]);

  if (!open) return null;
  const isEdit = !!initial?.id;

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/clients/${initial.id}`, form);
      else await api.post("/clients", form);
      toast.success(isEdit ? "Cliente atualizado" : "Cliente cadastrado");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" data-testid="client-dialog" onClick={(e)=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="close-client-dialog"><span aria-hidden>×</span></button>
        <span className="eyebrow">{isEdit ? "EDITAR CLIENTE" : "NOVO CLIENTE"}</span>
        <h2>{isEdit ? "Atualize os dados" : "Cadastro do cliente"}</h2>
        <p>Todas as informações ficam vinculadas às armas, processos e documentos.</p>
        <div className="form-grid">
          <label>Nome completo<input data-testid="client-name-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></label>
          <label>CPF<input data-testid="client-document-input" value={form.document} onChange={e=>setForm({...form,document:e.target.value})} placeholder="000.000.000-00"/></label>
          <label>Telefone<input data-testid="client-phone-input" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="(11) 99999-9999"/></label>
          <label>E-mail<input data-testid="client-email-input" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="cliente@email.com"/></label>
          <label className="span-2">Endereço<input data-testid="client-address-input" value={form.address} onChange={e=>setForm({...form,address:e.target.value})}/></label>
          <label className="span-2">Observações<textarea data-testid="client-notes-input" rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></label>
          {!isEdit && (
            <label className="span-2">Vincular ao portal (opcional)
              <input data-testid="client-portal-input" value={form.portal_user_email} onChange={e=>setForm({...form,portal_user_email:e.target.value})} placeholder="e-mail do usuário cliente já cadastrado"/>
            </label>
          )}
        </div>
        <button data-testid="save-client-button" className="primary-button full" disabled={busy} onClick={save}>{busy ? "Salvando..." : (isEdit ? "Salvar alterações" : "Cadastrar cliente")}</button>
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = async () => {
    try { const { data } = await api.get("/clients"); setClients(data); } catch {}
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    clients.filter(c => (c.name+" "+c.document+" "+(c.email||"")).toLowerCase().includes(query.toLowerCase())),
  [clients, query]);

  const remove = async (id) => {
    if (!confirm("Excluir este cliente e todos os registros vinculados?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Cliente removido"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/clients/${c.id}/portfolio.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `carteira_${c.name.replace(/\s+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Carteira em PDF gerada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">CARTEIRA</span>
          <h1>Clientes</h1>
          <p>Cadastre, edite e acompanhe cada cliente da sua consultoria.</p>
        </div>
        <button data-testid="add-client-button" className="primary-button" onClick={()=>setDialog({open:true, initial:null})}>
          <Plus size={17}/> Novo cliente
        </button>
      </div>
      <section className="panel full-panel">
        <div className="toolbar">
          <div className="search">
            <Search size={17}/>
            <input data-testid="clients-search-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Buscar por nome, CPF ou e-mail..."/>
          </div>
        </div>
        <div className="client-table">
          <div className="table-row table-header">
            <span>CLIENTE</span><span>ARMAS</span><span>PROCESSOS</span><span>CONTATO</span><span/>
          </div>
          {filtered.map(c => (
            <div className="table-row" data-testid={`client-detail-${c.id}`} key={c.id}>
              <div className="client-cell">
                <div className="avatar avatar-light">{initials(c.name)}</div>
                <div><b>{c.name}</b><small>{c.document || "—"}</small></div>
              </div>
              <span className="cell-icon"><Shield size={13}/> {c.weapons_count ?? 0}</span>
              <span className="cell-icon"><FileText size={13}/> {c.processes_count ?? 0}</span>
              <span className="small-cell">{c.phone || c.email || "—"}</span>
              <div className="row-actions">
                <button data-testid={`pdf-client-${c.id}`} className="icon-mini" title="Carteira em PDF" onClick={()=>downloadPdf(c)}><FileDown size={14}/></button>
                <button data-testid={`edit-client-${c.id}`} className="icon-mini" onClick={()=>setDialog({open:true, initial:c})}><Pencil size={14}/></button>
                <button data-testid={`delete-client-${c.id}`} className="icon-mini danger" onClick={()=>remove(c.id)}><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {!filtered.length && <div className="empty-inline">Nenhum cliente cadastrado ainda.</div>}
        </div>
      </section>
      <ClientDialog open={dialog.open} initial={dialog.initial} onClose={()=>setDialog({open:false, initial:null})} onSaved={load}/>
    </>
  );
}
